"use client";

import { useDiscountUpdate } from "@/hooks/useDiscountUpdate";
import { useAppSelector } from "@/store/hooks";
import FormFieldWrapper from "../formFieldWrapper";
import InputWithDropdown from "../inputWithDropdown";
import { Switch } from "@/components/ui/switch";
import SelectedProductsList from "../selectedProductsList";
import SearchProducts from "../searchProducts";
import { StandardFormInput, StandardFormSelect } from "@/components/common/StandardFormInput";
import SelectedCollectionsList from "../SelectedCollectionsList";
import React from "react";
import { toast } from "sonner";

const currentPage = "amountOffProducts";

const DiscountValueSection = () => {
  const handleUpdate = useDiscountUpdate();
  const {
    discount_type,
    discount_value_max,
    discount_value_fixed,
    discount_value_percentage,
    applicable_products_type,
    applicable_products,
    applicable_collections,
  } = useAppSelector((state) => state.discount[currentPage]);
  const isDiscountValueFixed = discount_type === "fixed";
  const [error, setError] = React.useState<string | null>(null);
  const handleDiscountFieldChange = ({
    field,
    value,
  }: {
    field: string;
    value: string;
  }) => {
    if (value.includes("-")) {
      setError("Negative values are not allowed.");
      handleUpdate(currentPage, { [field]: "" });
      return;
    }
    if (parseFloat(value) < 0) {
      setError("Negative values are not allowed.");
      handleUpdate(currentPage, { [field]: "" });
      return;
    }
    if (field === "discount_value_percentage") {
      if (parseFloat(value) > 100) {
        toast.error("Percentage cannot exceed 100%.");
        handleUpdate(currentPage, { [field]: "100" });
        return;
      }
    }
    setError(null);
    handleUpdate(currentPage, { [field]: value });
  };

  const handleDiscountValueDropdownChange = (value: string) => {
    handleDiscountFieldChange({
      field: "discount_type",
      value,
    });
  };

  return (
    <FormFieldWrapper title="Discount Value">
      <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(min(250px,100%),1fr))] gap-5">
        <InputWithDropdown
          value={
            isDiscountValueFixed
              ? discount_value_fixed.toString()
              : discount_value_percentage.toString()
          }
          inputProps={{
            placeholder: "0.00",
            min: 0,
            max: !isDiscountValueFixed ? 100 : undefined,
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              // Block minus, e, and other non-numeric keys except allowed ones
              if (
                e.key === "-" ||
                e.key === "e" ||
                e.key === "+" ||
                e.key === "E"
              ) {
                e.preventDefault();
              }
            },
          }}
          onInputChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            if (val.includes("-") || parseFloat(val) < 0) {
              handleDiscountFieldChange({
                field: isDiscountValueFixed
                  ? "discount_value_fixed"
                  : "discount_value_percentage",
                value: "",
              });
              return;
            }
            if (!isDiscountValueFixed && parseFloat(val) > 100) {
              toast.error("Percentage cannot exceed 100%.");
              handleDiscountFieldChange({
                field: "discount_value_percentage",
                value: "100",
              });
              return;
            }
            handleDiscountFieldChange({
              field: isDiscountValueFixed
                ? "discount_value_fixed"
                : "discount_value_percentage",
              value: val,
            });
          }}
          selectedDropdownValue={discount_type}
          onDropdownChange={handleDiscountValueDropdownChange}
          dropdownOptions={[
            {
              label: "Fixed Amount",
              value: "fixed",
            },
            {
              label: "Percentage",
              value: "percentage",
            },
          ]}
        />
      </div>
      
      <StandardFormInput
        label="Maximum discount value"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const val = e.target.value;
            if (val.includes("-") || parseFloat(val) < 0) {
              handleDiscountFieldChange({
                field: "discount_value_max",
                value: "",
              });
              return;
            }
            handleDiscountFieldChange({
              field: "discount_value_max",
              value: val,
            });
          }}
          type="number"
          placeholder="0"
          min={0}
          value={discount_value_max}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (
              e.key === "-" ||
              e.key === "e" ||
              e.key === "+" ||
              e.key === "E"
            ) {
              e.preventDefault();
            }
          }}
        error={error || undefined}
      />
      
      <SearchProducts
        applicableProductsType={applicable_products_type}
        currentPage={currentPage}
        applicableProductsKey="applicable_products"
        applicableCollectionsKey="applicable_collections"
      />
      <div className="space-y-1">
        <label className="block text-[13px] font-semibold text-foreground">
          Selected Products
        </label>
        {applicable_products.length > 0 && (
        <SelectedProductsList
          currentPage={currentPage}
          productsList={applicable_products}
        />
        )}
      </div>
      {applicable_products_type === "specificCollections" && (
        <SelectedCollectionsList
          currentPage={currentPage}
          collectionsList={applicable_collections}
        />
      )}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center space-x-5">
            <p className="text-[15px] font-semibold">Once Per Order</p>
            <Switch />
          </div>
          <span className="text-[13px] font-normal">
            If not selected, the amount will be taken off each eligible item in
            an order.
          </span>
        </div>
      </div>
    </FormFieldWrapper>
  );
};

export default DiscountValueSection;
